import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnInit, Output, ViewEncapsulation, forwardRef } from "@angular/core";
import { ControlValueAccessor, FormBuilder, FormControl, FormGroup, NG_VALUE_ACCESSOR } from "@angular/forms";
import { noop, takeUntil } from "rxjs";
import { DestroyService } from "@webapp/core/util/services/destroy.service";
import { localize } from "@webapp/localization/utils/localization.utils";
import { MetricTypeSelectorChange, MetricTypeSelectorOption } from "@webapp/okrs/metrics/components/metric-type-selector/metric-type-selector.models";
import { Metric } from "@webapp/okrs/metrics/models/metric.models";
import { generateOptions } from "@webapp/okrs/utils/metric.utils";
import { SimpleChangesTyped } from "@webapp/shared/models";

interface SelectFormFields {
  type: FormControl<string>;
}

@Component({
  selector: "metric-type-selector",
  templateUrl: "./metric-type-selector.component.html",
  styleUrls: ["./metric-type-selector.component.less"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  providers: [
    DestroyService,
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MetricTypeSelectorComponent),
      multi: true,
    },
  ],
})
export class MetricTypeSelectorComponent implements OnInit, OnChanges, ControlValueAccessor {
  @Input() public uiId: string;
  @Input() public a11yLabel: string;
  @Input() public a11yLabelledby: string;
  @Input() public a11yDescription: string;
  @Input() public disabled: boolean;

  @Output() public readonly selectChange = new EventEmitter<MetricTypeSelectorChange>();

  // You can pass metric to generate options or pass options directly
  @Input() public metric: Metric;
  @Input() public options: MetricTypeSelectorOption[];

  public selectForm: FormGroup<SelectFormFields>;
  private propagateChange: (input: MetricTypeSelectorChange) => void = noop;

  constructor(private formBuilder: FormBuilder, private destroyService$: DestroyService) {}

  public ngOnInit(): void {
    this.selectForm = this.formBuilder.group({
      type: this.formBuilder.control(""),
    });

    this.subscribeToValueChange();

    if (this.metric) {
      this.setOptions();
      this.patchTypeFormValue({
        value: {
          manualType: this.metric?.manualType,
          targetOperator: this.metric?.targetOperator,
        },
        shouldEmitChange: true,
      });
    }
  }

  public ngOnChanges(changes: SimpleChangesTyped<MetricTypeSelectorComponent>): void {
    this.handleMetricInputChanges(changes);
  }

  public writeValue(value: MetricTypeSelectorChange): void {
    this.patchTypeFormValue({ value, shouldEmitChange: false });
  }

  public registerOnChange(fn: (input: MetricTypeSelectorChange) => void): void {
    this.propagateChange = fn;
  }

  public registerOnTouched(): void {
    // required by the ControlValueAccessor interface
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  private handleMetricInputChanges(changes: SimpleChangesTyped<MetricTypeSelectorComponent>): void {
    if (this.selectForm && changes.metric) {
      this.setOptions();
      this.patchTypeFormValue({
        value: {
          manualType: this.metric?.manualType,
          targetOperator: this.metric?.targetOperator,
        },
        shouldEmitChange: false,
      });
    }
  }

  private setOptions(): void {
    this.options = generateOptions(this.metric);
  }

  private patchTypeFormValue({ value, shouldEmitChange }: { value: MetricTypeSelectorChange; shouldEmitChange?: boolean }): void {
    const patchValueParams = { emitEvent: shouldEmitChange };

    if (value.manualType === "boolean") {
      this.selectForm.get("type").patchValue(localize("achieved_or_not"), patchValueParams);
    } else {
      const option = this.options.find((it) => it.emitValue.targetOperator === value.targetOperator);

      if (option) {
        this.selectForm.get("type").patchValue(option.label, patchValueParams);
      }
    }
  }

  private subscribeToValueChange(): void {
    this.selectForm.controls.type.valueChanges.pipe(takeUntil(this.destroyService$)).subscribe((value) => {
      const selectionOption = this.getOptionByLabel(value);

      if (selectionOption) {
        this.selectChange.emit(selectionOption.emitValue);
        this.propagateChange(selectionOption.emitValue);
      }
    });
  }

  private getOptionByLabel(label: string): MetricTypeSelectorOption {
    return this.options.find((option) => option.label === label);
  }
}
